import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTreeData } from '../hooks/useTreeData';
import { useZoomState } from '../hooks/useZoomState';
import { useTreeInteraction } from '../hooks/useTreeInteraction';
import { useTouchGestures } from '../hooks/useTouchGestures';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import TreeNode from '../components/TreeNode';
import EmptyNode from '../components/EmptyNode';
import NodeDetailsPanel from '../components/NodeDetailsPanel';
import DetailsBottomSheet from '../components/DetailsBottomSheet';
import TreeSearch from '../components/TreeSearch';
import PlacementHeatmap from '../components/PlacementHeatmap';
import PlacementWelcomeModal from '../components/PlacementWelcomeModal';
import { layoutTree, calculateBounds, fitViewport } from '../utils/treeLayout';

// Get node size based on zoom level (must match TreeNode.jsx logic --
// nodes are wide pills now, not circles, to fit a full 10-char Wellis ID)
const getNodeSize = (zoom) => {
  if (zoom < 0.5) return { width: 44, height: 20 };
  if (zoom < 1) return { width: 64, height: 28 };
  if (zoom < 1.5) return { width: 88, height: 36 };
  return { width: 110, height: 44 };
};

export default function Genealogy() {
  const { user, loading: authLoading } = useAuth();
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [placementDialog, setPlacementDialog] = useState(null);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [placingUser, setPlacingUser] = useState(null);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  // Detail panel state: intentionally decoupled from selectedNodeId so closing
  // the panel doesn't lose the selection, and selecting a node doesn't require
  // closing the current view first.
  const [desktopDetailOpen, setDesktopDetailOpen] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [packageStatus, setPackageStatus] = useState(null);
  // Re-root navigation: clicking a node with children makes it the new
  // top-of-screen "view root", showing its own MAX_VISIBLE_DEPTH levels
  // below it instead of trying to fit the whole tree at once.
  // viewPath is the breadcrumb trail from the true root down to the
  // current view root (true root itself is NOT included -- it's always
  // implicitly the first crumb, rendered separately).
  const [viewPath, setViewPath] = useState([]); // [{ nodeId, label }]
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const viewportRef = useRef(null);

  // Inject webkit scrollbar styling for status row
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .status-scroll::-webkit-scrollbar {
        height: 6px;
      }
      .status-scroll::-webkit-scrollbar-track {
        background: transparent;
      }
      .status-scroll::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.3);
        border-radius: 3px;
      }
      .status-scroll::-webkit-scrollbar-thumb:hover {
        background: rgba(0, 0, 0, 0.5);
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const treeData = useTreeData(tree);
  const zoomState = useZoomState();
  const responsive = useResponsiveLayout();

  useTreeInteraction(viewportRef, zoomState);
  useTouchGestures(containerRef, zoomState);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Fetch package status to determine if user can view genealogy
    const fetchPackageStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/package-status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setPackageStatus(data);
          // Fetch tree if verified or if user is superadmin (exempt from package gate)
          if (data.verified || user.role === 'superadmin') {
            // Check if should show placement welcome modal
            const hidePlacementWelcome = localStorage.getItem('hidePlacementWelcome');
            if (user.status === 'approved' && !hidePlacementWelcome) {
              setShowWelcomeModal(true);
            }
            fetchTree();
          } else {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch package status:', err);
        setLoading(false);
      }
    };
    
    fetchPackageStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, navigate]);

  const fetchTree = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/mlm/tree/genealogy`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch genealogy tree');
      }

      const data = await response.json();
      console.log('Tree data loaded:', {
        me: data.me,
        downlineCount: data.downline?.length || 0,
        downline: data.downline,
      });
      setTree(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Superadmin sees every approved/pre-processed unplaced user org-wide; anyone else
      // can only place their own referrals (backend authorization matches: place-user.js
      // allows superadmin OR the referrer).
      const isSuperAdmin = user?.role === 'superadmin';
      const endpoint = isSuperAdmin
        ? '/api/mlm/tree/unplaced-users'
        : '/api/user/my-referrals';

      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (isSuperAdmin) {
          setApprovedUsers(data.unplaced_users || []);
          return data.unplaced_users || [];
        }
        // Normalize my-referrals.js's shape to what the placement dialog expects
        const mapped = (data.referrals || []).map((r) => ({
          id: r.id,
          email: r.email,
          wellis_id: r.affiliate_code,
          name: r.first_name && r.last_name ? `${r.first_name} ${r.last_name}` : (r.first_name || r.last_name || r.email),
          created_at: r.created_at,
          pre_processed: r.pre_processed,
        }));
        setApprovedUsers(mapped);
        return mapped;
      }
      return [];
    } catch (err) {
      console.error('Failed to fetch approved users:', err);
      return [];
    }
  };

  const handlePlaceUser = async (parentId, leg, slotId) => {
    try {
      const users = await fetchApprovedUsers();
      // Check if there are any approved unplaced users
      if (!users || users.length === 0) {
        setError('No approved and unplaced users available for placement');
        return;
      }
      setPlacementDialog({ parentId, leg, slotId });
    } catch (err) {
      setError('Failed to load available users');
    }
  };

  const confirmPlacement = async (userId) => {
    if (!placementDialog) return;

    try {
      setPlacingUser(userId);
      const token = localStorage.getItem('token');

      console.log('[Genealogy] Placing user', {
        userId,
        parentId: placementDialog.parentId,
        parentIdType: typeof placementDialog.parentId,
        leg: placementDialog.leg,
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/mlm/tree/place-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          parent_node_id: placementDialog.parentId,
          leg_position: placementDialog.leg,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to place user');
      }

      setPlacementDialog(null);
      await fetchTree();
    } catch (err) {
      setError(err.message);
    } finally {
      setPlacingUser(null);
    }
  };

  // Depth-limited view: 4 levels deep at a time on desktop, 3 on mobile
  // (root itself = level 0), to stop far-downline trees from cramming
  // every level into one crowded layout. Nodes past the limit start
  // auto-collapsed; a manual click on the existing collapse/expand
  // toggle (zoomState.collapsedNodes) still expands that specific
  // branch one more level -- same mechanism as manual collapsing
  // today, just seeded with a depth-based default instead of starting
  // empty. Root's own ancestor chain has length 0, so a node at depth
  // D has getAncestors(id).length === D.
  const MAX_VISIBLE_DEPTH = 2;
  // Re-rooted view: the current view root is whatever's on top of the
  // breadcrumb stack, or the true tree root if the stack is empty.
  const viewRootNodeId = viewPath.length > 0 ? viewPath[viewPath.length - 1].nodeId : treeData.rootNodeId;
  const effectiveCollapsedNodes = new Set(zoomState.collapsedNodes);
  if (viewRootNodeId) {
    // A node is auto-collapsed if it sits MAX_VISIBLE_DEPTH levels
    // below the CURRENT VIEW ROOT (not necessarily the true tree root
    // -- re-rooting via a breadcrumb click changes what "depth 0"
    // means). A manual collapse/expand toggle can still hide/reveal a
    // specific branch within the visible window.
    treeData.nodeMap.forEach((_, nodeId) => {
      if (nodeId === viewRootNodeId) return;
      if (zoomState.manuallyExpandedNodes.has(nodeId)) return;
      const ancestors = treeData.getAncestors(nodeId); // root-first order, from the TRUE root
      const viewRootIndex = ancestors.indexOf(viewRootNodeId);
      // Not a descendant of the current view root at all -- not part of
      // this view (shouldn't normally happen since layoutTree only
      // recurses into the view root's own subtree, but guards against a
      // stale viewPath right after a tree refetch).
      if (viewRootIndex === -1) return;
      const sinceViewRoot = ancestors.length - (viewRootIndex + 1);
      // MAX_VISIBLE_DEPTH counts total rendered rows INCLUDING the view
      // root itself (view root = row 0). So MAX_VISIBLE_DEPTH=2 means
      // rows 0 and 1 render normally, and row 1's nodes collapse (render
      // as a terminal leaf, hiding their own children at row 2+).
      if (sinceViewRoot >= MAX_VISIBLE_DEPTH - 1) {
        effectiveCollapsedNodes.add(nodeId);
      }
    });
  }

  const handleNodeReRoot = (nodeId) => {
    const node = treeData.getNode(nodeId);
    if (!node) return;
    // Every placed node has 3 leg slots underneath it, even if none are
    // currently occupied (e.g. right after an admin removes the last
    // child there) -- re-rooting is always meaningful since it reveals
    // those slots (for placement) rather than only existing children,
    // so this doesn't gate on treeData.getChildren(nodeId).length.
    setViewPath((prev) => [...prev, { nodeId, label: node.affiliate_code || node.user_name || 'Node' }]);
  };

  const handleBreadcrumbClick = (index) => {
    // index === -1 means "back to true root" (clear the whole path);
    // otherwise truncate the path to end at that crumb.
    if (index < 0) {
      setViewPath([]);
    } else {
      setViewPath((prev) => prev.slice(0, index + 1));
    }
  };

  // Get actual node dimensions based on current zoom level -- computed
  // BEFORE layoutTree so the spacing math can use the real rendered
  // circle diameter (20/40/80/100px depending on zoom) instead of a
  // fixed 50px assumption that only matched the lowest zoom levels.
  const nodeSize = getNodeSize(zoomState.zoom);
  const NODE_WIDTH = nodeSize.width;
  const NODE_HEIGHT = nodeSize.height;

  const { nodePositions, emptySlots, layoutItems } = layoutTree(treeData, effectiveCollapsedNodes, true, responsive.isMobile, NODE_WIDTH, viewRootNodeId);
  const bounds = calculateBounds(nodePositions, 80, layoutItems);
  const canAutoFit = nodePositions.size > 0;
  const selectedNode = zoomState.selectedNodeId ? treeData.getNode(zoomState.selectedNodeId) : null;
  const DEBUG = false;  // Toggle debug visualizations

  // Build parent -> children map from layoutItems for connector rendering
  const childrenByParent = new Map();
  layoutItems.forEach(item => {
    if (!childrenByParent.has(item.parentId)) {
      childrenByParent.set(item.parentId, []);
    }
    childrenByParent.get(item.parentId).push(item);
  });

  // Debug: Log rendering details
  if (DEBUG) {
    console.log('\n🎨 GENEALOGY RENDERING:');
    console.log(`   Total nodes to render: ${Array.from(treeData.nodeMap.values()).length}`);
    console.log(`   Empty slots to render: ${layoutItems.filter(i => !i.occupied).length}`);
    console.log(`   Total connectors: ${layoutItems.length}`);
    console.log(`   Filled connectors: ${layoutItems.filter(i => i.occupied).length}`);
    console.log(`   Dashed connectors: ${layoutItems.filter(i => !i.occupied).length}\n`);
  }

  // DOM Inspection Hook - runs after render completes (guarded by DEBUG flag)
  useEffect(() => {
    if (!containerRef.current || !DEBUG) return;

    setTimeout(() => {
      console.log('\n════════════════════════════════════════');
      console.log('🔍 DOM HIERARCHY & COORDINATE INSPECTION');
      console.log('════════════════════════════════════════\n');

      // 1. Container hierarchy
      const viewport = viewportRef.current;
      const container = containerRef.current;
      const svg = container?.querySelector('svg');

      console.log('📍 DOM HIERARCHY:');
      console.log('  viewportRef (overflow: hidden):', {
        classes: viewport?.className,
        rect: viewport ? {
          left: viewport.getBoundingClientRect().left,
          top: viewport.getBoundingClientRect().top,
          width: viewport.getBoundingClientRect().width,
          height: viewport.getBoundingClientRect().height,
        } : null,
      });

      console.log('  containerRef (with pan/zoom transform):', {
        classes: container?.className,
        computedStyle: container ? {
          position: window.getComputedStyle(container).position,
          transform: window.getComputedStyle(container).transform,
          transformOrigin: window.getComputedStyle(container).transformOrigin,
        } : null,
        rect: container ? {
          left: container.getBoundingClientRect().left,
          top: container.getBoundingClientRect().top,
          width: container.getBoundingClientRect().width,
          height: container.getBoundingClientRect().height,
        } : null,
      });

      // 2. SVG inspection
      console.log('\n📐 SVG PROPERTIES:');
      if (svg) {
        const svgStyle = window.getComputedStyle(svg);
        const svgRect = svg.getBoundingClientRect();
        const svgViewBox = svg.getAttribute('viewBox');
        const svgPreserveAspectRatio = svg.getAttribute('preserveAspectRatio');

        console.log('  SVG attributes:', {
          width: svg.getAttribute('width'),
          height: svg.getAttribute('height'),
          viewBox: svgViewBox,
          preserveAspectRatio: svgPreserveAspectRatio || '(not set)',
        });

        console.log('  SVG computed style:', {
          position: svgStyle.position,
          left: svgStyle.left,
          top: svgStyle.top,
          transform: svgStyle.transform,
        });

        console.log('  SVG bounding rect (screen coordinates):', {
          left: svgRect.left,
          top: svgRect.top,
          width: svgRect.width,
          height: svgRect.height,
          right: svgRect.right,
          bottom: svgRect.bottom,
        });

        // 3. Debug circles in SVG
        const circles = svg.querySelectorAll('circle[fill="red"], circle[fill="blue"]');
        console.log(`\n🔴 DEBUG CIRCLES: found ${circles.length}`);
        circles.forEach((circle, idx) => {
          const cx = circle.getAttribute('cx');
          const cy = circle.getAttribute('cy');
          const fill = circle.getAttribute('fill');
          const rect = circle.getBoundingClientRect();

          console.log(`  Circle ${idx} (${fill}):`, {
            svgCoords: { cx: parseFloat(cx), cy: parseFloat(cy) },
            screenRect: {
              left: rect.left,
              top: rect.top,
              centerX: rect.left + rect.width / 2,
              centerY: rect.top + rect.height / 2,
            },
          });
        });
      }

      // 4. HTML nodes
      const nodeElements = container?.querySelectorAll('[style*="position: absolute"]');
      if (nodeElements && nodeElements.length > 0) {
        console.log(`\n📍 HTML NODES: found ${nodeElements.length}`);
        Array.from(nodeElements).slice(0, 3).forEach((node, idx) => {
          const style = window.getComputedStyle(node);
          const rect = node.getBoundingClientRect();
          const left = style.left;
          const top = style.top;

          console.log(`  Node ${idx}:`, {
            cssLeftTop: { left, top },
            screenRect: {
              left: rect.left,
              top: rect.top,
              centerX: rect.left + rect.width / 2,
              centerY: rect.top + rect.height / 2,
              width: rect.width,
              height: rect.height,
            },
          });
        });
      }

      // 5. Bounds rectangle (magenta debug box)
      const boundsRect = svg?.querySelector('rect[stroke="magenta"]');
      if (boundsRect) {
        const x = boundsRect.getAttribute('x');
        const y = boundsRect.getAttribute('y');
        const width = boundsRect.getAttribute('width');
        const height = boundsRect.getAttribute('height');
        const rect = boundsRect.getBoundingClientRect();

        console.log('\n🟪 BOUNDS RECTANGLE (magenta debug):');
        console.log('  SVG coords (viewBox space):', {
          x: parseFloat(x),
          y: parseFloat(y),
          width: parseFloat(width),
          height: parseFloat(height),
        });
        console.log('  Screen rect (getBoundingClientRect):', {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        });
      }

      // 6. Origin crosshair
      const originCircle = svg?.querySelector('circle[fill="cyan"]');
      if (originCircle) {
        const cx = originCircle.getAttribute('cx');
        const cy = originCircle.getAttribute('cy');
        const rect = originCircle.getBoundingClientRect();

        console.log('\n🔵 ORIGIN CROSSHAIR (cyan, should be at tree 0,0):');
        console.log('  SVG coords:', { cx: parseFloat(cx), cy: parseFloat(cy) });
        console.log('  Screen center:', {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        });
      }

      // 7. Zoom state
      console.log('\n⚙️ ZOOM STATE:');
      console.log('  Pan:', zoomState.pan);
      console.log('  Zoom:', zoomState.zoom);

      console.log('\n════════════════════════════════════════\n');
    }, 100);  // Small delay to ensure DOM is fully painted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodePositions.size, zoomState.pan, zoomState.zoom]);

  // Auto-center root node on initial load
  useEffect(() => {
    if (canAutoFit && tree && treeData.rootNodeId && viewportRef.current) {
      const rootPos = nodePositions.get(treeData.rootNodeId);
      if (rootPos) {
        const { offsetWidth } = viewportRef.current;
        const initialZoom = 0.83; // 83% initial zoom
        zoomState.setZoom(initialZoom);
        // Use rendered node size based on initial zoom level
        const renderedSize = getNodeSize(initialZoom);
        const nodeSize = renderedSize.width;
        const topGap = 10; // Top gap from viewport to root
        const newPan = {
          x: offsetWidth / 2 - rootPos.x * initialZoom,
          y: nodeSize / 2 + topGap - rootPos.y * initialZoom,
        };
        zoomState.setPan(newPan);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAutoFit, tree, treeData.rootNodeId]);

  const handleFitToView = () => {
    if (!viewportRef.current) return;
    const { offsetWidth, offsetHeight } = viewportRef.current;
    const { zoom, pan } = fitViewport(bounds, offsetWidth, offsetHeight);
    zoomState.setZoom(zoom);
    zoomState.setPan(pan);
  };

  const handleResetView = () => {
    const resetZoom = 0.83;
    zoomState.setZoom(resetZoom);
    // Position root node using same logic as initial load
    if (viewportRef.current && treeData.rootNodeId) {
      const rootPos = nodePositions.get(treeData.rootNodeId);
      if (rootPos) {
        const { offsetWidth } = viewportRef.current;
        const renderedSize = getNodeSize(resetZoom);
        const nodeSize = renderedSize.width;
        const topGap = 10;
        zoomState.setPan({
          x: offsetWidth / 2 - rootPos.x * resetZoom,
          y: nodeSize / 2 + topGap - rootPos.y * resetZoom,
        });
      }
    }
  };

  const handleCenterOnNode = (nodeId) => {
    if (!viewportRef.current) return;
    const pos = nodePositions.get(nodeId);
    if (!pos) return;

    const { offsetWidth } = viewportRef.current;
    const centerX = offsetWidth / 2;

    // Use same positioning as initial load: node appears at consistent distance from top
    // Get rendered node size based on current zoom level
    const renderedSize = getNodeSize(zoomState.zoom);
    const nodeSize = renderedSize.width;
    const topGap = 10; // Same gap as initial load

    // Position clicked node at same distance from top as root on initial load
    zoomState.setPan({
      x: centerX - pos.x * zoomState.zoom,
      y: nodeSize / 2 + topGap - pos.y * zoomState.zoom,
    });
  };

  // Centers the viewport on local (0, 0) -- layoutTree always places
  // whatever node it's laying out FROM (layoutRootId) at local (0, 0),
  // so this is the correct pan target for a node that just became the
  // new view root via re-rooting, without needing a nodePositions
  // lookup. handleCenterOnNode can't be reused here: it reads the
  // CURRENT nodePositions, which at the moment of the click still
  // reflects the OLD (pre-re-root) layout -- setViewPath's state update
  // (and the resulting layoutTree() re-run) hasn't happened yet, so the
  // old position is for the wrong coordinate space and pans far off
  // from where the newly re-rooted tree actually renders.
  const handleCenterOnReRoot = () => {
    if (!viewportRef.current) return;
    const { offsetWidth } = viewportRef.current;
    const renderedSize = getNodeSize(zoomState.zoom);
    const nodeSize = renderedSize.width;
    const topGap = 10;
    zoomState.setPan({
      x: offsetWidth / 2,
      y: nodeSize / 2 + topGap,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-on-background">Loading genealogy...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col ${responsive.isMobile ? 'pt-16' : 'pt-24'}`} style={{ backgroundColor: '#1a3a2a' }}>

      {error && (
        <div className="border-b text-error p-4 text-body-sm w-full" style={{ backgroundColor: '#152214', borderColor: '#c9a961' }}>
          {error}
        </div>
      )}

      {!packageStatus?.verified && user.role !== 'superadmin' ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="border rounded-lg p-8 text-center max-w-md mx-margin-mobile" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <p className="mb-4" style={{ color: '#b8a68f' }}>Purchase a package to activate your membership and become eligible for genealogy placement and team earnings.</p>
            <button
              onClick={() => navigate('/packages')}
              className="px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity text-black text-body-sm"
              style={{ backgroundColor: '#c9a961' }}
            >
              View Packages
            </button>
          </div>
        </div>
      ) : !tree || !tree.me ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-body-md text-on-surface/60">Loading your genealogy...</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* User Info Bar - Compact on Mobile */}
          <div className={`bg-primary text-on-primary px-margin-mobile ${responsive.isMobile ? 'py-1.5 h-12' : 'py-4'}`}>
            <div className="max-w-container-max mx-auto flex items-center gap-2">
              {responsive.isMobile ? (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="text-headline-sm font-medium truncate">
                      {tree.me.affiliate_code}
                    </div>
                  </div>
                  <div className="text-body-xs opacity-80 whitespace-nowrap">
                    {tree.me.email?.split('@')[0] || 'User'}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex flex-col gap-1">
                    <div className="text-headline-sm font-medium">Genealogy Tree</div>
                    <div className="text-body-sm opacity-80">Your Affiliate Code</div>
                  </div>
                  <div className="text-center flex-1">
                    <div className="font-mono font-medium text-headline-md">
                      {tree.me.affiliate_code}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-0">
                    <div className="text-headline-sm font-medium">Master Admin</div>
                    <div className="text-body-sm opacity-80">{tree.me.email}</div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Mobile Toolbar - Compact */}
          {responsive.isMobile && (
            <div className="bg-surface border-b border-outline px-margin-mobile py-2 h-12 flex items-center gap-2 text-body-xs">
              {/* Collapsible Search */}
              {showMobileSearch ? (
                <div className="flex-1 flex items-center gap-1 bg-background rounded px-2 py-1">
                  <span>🔍</span>
                  <TreeSearch
                    treeData={treeData}
                    onSelect={(nodeId) => {
                      zoomState.setSelectedNodeId(nodeId);
                      setShowMobileSearch(false);
                    }}
                    onCenterNode={handleCenterOnNode}
                    isCompact={true}
                  />
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setShowMobileSearch(true)}
                    className="px-2 py-1 text-on-background"
                    title="Search"
                  >
                    🔍
                  </button>
                  <span className="text-on-background/60">
                    {Math.round(zoomState.zoom * 100)}%
                  </span>
                  <button
                    onClick={handleFitToView}
                    className="px-2 py-1 text-on-background"
                    title="Fit to view"
                  >
                    ⌂
                  </button>
                </>
              )}
            </div>
          )}

          {/* Desktop Controls */}
          {!responsive.isMobile && (
            <div className="border-b px-margin-mobile py-4" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <div className="max-w-container-max mx-auto">
                <div className="grid grid-cols-3 gap-4 mb-3">
                  {/* Left: Zoom Controls */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={zoomState.zoomOut}
                      className="px-3 py-1 rounded hover:opacity-90 text-body-sm text-black"
                      style={{ backgroundColor: '#c9a961' }}
                      title="Zoom out (−)"
                    >
                      −
                    </button>
                    <span className="w-14 text-center text-body-sm font-medium" style={{ color: '#c9a961' }}>{Math.round(zoomState.zoom * 100)}%</span>
                    <button
                      onClick={zoomState.zoomIn}
                      className="px-3 py-1 rounded hover:opacity-90 text-body-sm text-black"
                      style={{ backgroundColor: '#c9a961' }}
                      title="Zoom in (+)"
                    >
                      +
                    </button>

                    <div className="w-px h-6 mx-2" style={{ backgroundColor: '#c9a961' }}></div>

                    {canAutoFit && (
                      <button
                        onClick={handleFitToView}
                        className="px-3 py-1 rounded hover:opacity-90 text-body-sm text-black"
                        style={{ backgroundColor: '#c9a961' }}
                        title="Fit tree to view"
                      >
                        Fit
                      </button>
                    )}

                    <button
                      onClick={handleResetView}
                      className="px-3 py-1 rounded hover:opacity-90 text-body-sm text-black"
                      style={{ backgroundColor: '#c9a961' }}
                      title="Reset view"
                    >
                      Reset
                    </button>
                  </div>

                  {/* Center: Search */}
                  <div className="flex justify-center">
                    <div className="w-full max-w-xs">
                      <TreeSearch
                        treeData={treeData}
                        onSelect={zoomState.setSelectedNodeId}
                        onCenterNode={handleCenterOnNode}
                      />
                    </div>
                  </div>

                  {/* Right: Display Options */}
                  <div className="flex items-center justify-end gap-2">
                    <label className="flex items-center gap-2 text-body-sm cursor-pointer" style={{ color: '#c9a961' }}>
                      <input
                        type="checkbox"
                        checked={showHeatmap}
                        onChange={(e) => setShowHeatmap(e.target.checked)}
                        className="w-4 h-4 rounded"
                      />
                      Depth Heatmap
                    </label>

                    <span className="text-body-xs" style={{ color: '#b8a68f' }}>
                      {selectedNode ? `node ${selectedNode.node_id}` : `${nodePositions.size} nodes`}
                    </span>
                  </div>
                </div>

                {/* Status row: selected node summary + quick detail link */}
                <div className="flex items-center gap-4 text-body-xs border-t pt-2 mt-2" style={{ color: '#b8a68f', borderColor: '#c9a961' }}>
                  {selectedNode ? (
                    <>
                      <div className="flex-1 overflow-x-auto min-w-0 px-2 py-1 scroll-smooth status-scroll" style={{scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,0,0,0.3) transparent'}}>
                        <span className="whitespace-nowrap">
                          Selected: <strong>{selectedNode.user_name || selectedNode.user_email}</strong> — Leg {selectedNode.leg_position}, Level {selectedNode.depth}
                        </span>
                      </div>
                      <button
                        onClick={() => setDesktopDetailOpen(true)}
                        className="hover:underline flex-shrink-0"
                        style={{ color: '#c9a961' }}
                      >
                        View full details →
                      </button>
                    </>
                  ) : (
                    <span>Select a node to view details</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Re-root breadcrumb trail -- shown whenever the view has
              drilled in past the true tree root. */}
          {viewPath.length > 0 && (
            <div
              className="flex items-center gap-1 px-4 py-2 text-body-xs overflow-x-auto flex-shrink-0"
              style={{ backgroundColor: '#152214', color: '#b8a68f', borderBottom: '1px solid rgba(201,169,97,0.3)' }}
            >
              <button
                type="button"
                onClick={() => { handleBreadcrumbClick(-1); handleCenterOnReRoot(); }}
                className="hover:underline whitespace-nowrap flex-shrink-0"
                style={{ color: '#c9a961' }}
              >
                Root
              </button>
              {viewPath.map((crumb, idx) => (
                <React.Fragment key={crumb.nodeId}>
                  <span className="flex-shrink-0" style={{ color: '#999999' }}>›</span>
                  {idx === viewPath.length - 1 ? (
                    <span className="whitespace-nowrap flex-shrink-0" style={{ color: '#b8a68f' }}>{crumb.label}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { handleBreadcrumbClick(idx); handleCenterOnReRoot(); }}
                      className="hover:underline whitespace-nowrap flex-shrink-0"
                      style={{ color: '#c9a961' }}
                    >
                      {crumb.label}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Main Tree Viewport */}
          <div className={`flex flex-col flex-1 overflow-hidden gap-0`}>
            {/* Tree Canvas Area */}
            <div className={`flex flex-1 overflow-hidden gap-0`}>
            {/* Scroll Controls (Left Side) */}
            {!responsive.isMobile && (
              <div className="flex flex-col items-center justify-center gap-2 px-2 py-4 border-r" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                <button
                  onClick={() => zoomState.setPan({ ...zoomState.pan, y: zoomState.pan.y + 80 })}
                  className="p-2 rounded hover:opacity-90 transition-opacity text-black"
                  style={{ backgroundColor: '#c9a961' }}
                  title="Scroll up"
                >
                  ▲
                </button>
                <button
                  onClick={() => zoomState.setPan({ ...zoomState.pan, y: zoomState.pan.y - 80 })}
                  className="p-2 rounded hover:opacity-90 transition-opacity text-black"
                  style={{ backgroundColor: '#c9a961' }}
                  title="Scroll down"
                >
                  ▼
                </button>
              </div>
            )}

            {/* Tree Canvas */}
            <div
              ref={viewportRef}
              className="flex-1 overflow-hidden relative"
              style={{ cursor: 'grab', backgroundColor: '#1a3a2a' }}
            >
              {/* Placement Heatmap (Depth + Availability) */}
              {showHeatmap && (
                <PlacementHeatmap
                  nodePositions={nodePositions}
                  emptySlots={emptySlots}
                  treeData={treeData}
                  isVisible={showHeatmap}
                />
              )}

              {/* Tree Container */}
              <div
                ref={containerRef}
                className="w-full h-full relative"
                style={{
                  transformOrigin: '0 0',
                  transform: `translate(${zoomState.pan.x}px, ${zoomState.pan.y}px) scale(${zoomState.zoom})`,
                  transition: 'transform 0.1s ease-out',
                  touchAction: 'manipulation',
                }}
              >
                {/* SVG Lines for Tree Structure */}
                {nodePositions.size > 0 && (
                  <svg
                    className="absolute pointer-events-none"
                    width={bounds.width + 80}
                    height={bounds.height + 80}
                    viewBox={`${bounds.minX - 40} ${bounds.minY - 40} ${bounds.width + 80} ${bounds.height + 80}`}
                    style={{
                      left: bounds.minX - 40,
                      top: bounds.minY - 40,
                    }}
                  >
                    {/* DEBUG: Bounds rectangle */}
                    {DEBUG && (
                      <rect
                        x={bounds.minX}
                        y={bounds.minY}
                        width={bounds.width}
                        height={bounds.height}
                        fill="none"
                        stroke="magenta"
                        strokeDasharray="6 6"
                        strokeWidth="2"
                      />
                    )}

                    {/* DEBUG: Origin (0,0) crosshair - represents tree coordinate (0,0) */}
                    {DEBUG && (
                      <>
                        <line x1={-50} y1={0} x2={50} y2={0} stroke="cyan" strokeWidth="2" />
                        <line x1={0} y1={-50} x2={0} y2={50} stroke="cyan" strokeWidth="2" />
                        <circle cx={0} cy={0} r="3" fill="cyan" />
                        <text x={5} y={-5} fontSize="12" fill="cyan" fontWeight="bold">
                          (0,0)
                        </text>
                      </>
                    )}

                    {/* Draw connectors for all child slots (occupied and empty) */}
                    {Array.from(childrenByParent.entries()).map(([parentId, childItems]) => {
                      if (effectiveCollapsedNodes.has(parentId)) return null;
                      const parentPos = nodePositions.get(parentId);
                      if (!parentPos) return null;

                      return childItems.map((childItem) => {
                        const lineKey = `line-${childItem.id}`;
                        const isOccupied = childItem.occupied;

                        // Connect from parent's bottom-center to child's top-center
                        const startX = parentPos.x + NODE_WIDTH / 2;
                        const startY = parentPos.y + NODE_HEIGHT;  // Parent's bottom edge
                        const endX = childItem.x + NODE_WIDTH / 2;
                        const endY = childItem.y;  // Child's top edge

                        return (
                          <g key={lineKey}>
                            {/* Connector line: solid for occupied, dashed for empty */}
                            <line
                              x1={startX}
                              y1={startY}
                              x2={endX}
                              y2={endY}
                              stroke="#6f7d6b"
                              strokeWidth="2"
                              opacity={isOccupied ? 0.8 : 0.4}
                              strokeDasharray={isOccupied ? undefined : "6 6"}
                            />

                            {/* DEBUG: Endpoint circles */}
                            {DEBUG && (
                              <>
                                <circle cx={startX} cy={startY} r="6" fill="red" opacity="0.7" />
                                <circle cx={endX} cy={endY} r="6" fill={isOccupied ? "blue" : "orange"} opacity="0.7" />
                              </>
                            )}
                          </g>
                        );
                      });
                    })}
                  </svg>
                )}

                {/* Render Nodes */}
                {Array.from(treeData.nodeMap.values()).map((node) => {
                  const pos = nodePositions.get(node.node_id);
                  if (!pos) return null;

                  return (
                    <div key={node.node_id} style={{ position: 'absolute', left: pos.x, top: pos.y, transform: 'translate(-50%, -50%)' }}>
                      <TreeNode
                        ref={(el) => {
                          // Diagnostic: measure actual rendered node center
                          if (el && DEBUG) {
                            const nodeId = node.node_id;
                            const isTargetNode = nodeId === 4 || nodeId === 5;

                            if (isTargetNode) {
                              console.log(`🔍 Ref callback for node ${nodeId}, element mounted`);

                              const rect = el.getBoundingClientRect();
                              const htmlCenterX = rect.left + rect.width / 2;
                              const htmlCenterY = rect.top + rect.height / 2;

                              console.log(`📏 NODE ${nodeId} DIMENSIONS:`);
                              console.log(`   Width: ${Math.round(rect.width)}px, Height: ${Math.round(rect.height)}px`);
                              console.log(`   Screen Position: left=${Math.round(rect.left)}, top=${Math.round(rect.top)}`);
                              console.log(`   Visual Center: X=${Math.round(htmlCenterX)}, Y=${Math.round(htmlCenterY)}`);

                              // Use setTimeout to allow SVG circles to render
                              setTimeout(() => {
                                const svg = document.querySelector('svg[viewBox*="-300"]');
                                if (!svg) {
                                  console.log(`   ⚠️ Could not find SVG`);
                                  return;
                                }

                                const allCircles = svg.querySelectorAll('circle');
                                console.log(`   Found ${allCircles.length} total circles in SVG`);

                                // Get all red circles (parent endpoints)
                                const redCircles = Array.from(allCircles).filter(c => c.getAttribute('fill') === 'red');
                                console.log(`   Found ${redCircles.length} red circles (parent endpoints)`);

                                // Get all blue circles (occupied child endpoints)
                                const blueCircles = Array.from(allCircles).filter(c => c.getAttribute('fill') === 'blue');
                                console.log(`   Found ${blueCircles.length} blue circles (occupied child endpoints)`);

                                // Get all orange circles (empty slot endpoints)
                                const orangeCircles = Array.from(allCircles).filter(c => c.getAttribute('fill') === 'orange');
                                console.log(`   Found ${orangeCircles.length} orange circles (empty slot endpoints)`);

                                let svgCircle = null;
                                if (nodeId === 4 && redCircles.length > 0) {
                                  svgCircle = redCircles[0];
                                  console.log(`   Selected: first red circle`);
                                } else if (nodeId === 5 && blueCircles.length > 0) {
                                  svgCircle = blueCircles[0];
                                  console.log(`   Selected: first blue circle`);
                                }

                                if (svgCircle) {
                                  const svgRect = svgCircle.getBoundingClientRect();
                                  const svgCenterX = svgRect.left + svgRect.width / 2;
                                  const svgCenterY = svgRect.top + svgRect.height / 2;
                                  const deltaX = htmlCenterX - svgCenterX;
                                  const deltaY = htmlCenterY - svgCenterY;

                                  console.log(`🎯 SVG CIRCLE CENTER: X=${Math.round(svgCenterX)}, Y=${Math.round(svgCenterY)}`);
                                  console.log(`📊 DELTA: ΔX=${Math.round(deltaX)}px, ΔY=${Math.round(deltaY)}px`);
                                  console.log(`════════════════════════════════════════`);
                                } else {
                                  console.log(`   ⚠️ Could not find matching circle`);
                                }
                              }, 100);
                            }
                          }
                        }}
                        node={node}
                        zoom={zoomState.zoom}
                        isCollapsed={effectiveCollapsedNodes.has(node.node_id)}
                        onCollapse={(nodeId) => {
                          // A node manually collapsed by the user (present
                          // in collapsedNodes itself) toggles the normal
                          // way; a node that's collapsed only because of
                          // the depth-limit default toggles via
                          // manuallyExpandedNodes instead, so expanding it
                          // doesn't fight with the depth default on the
                          // next render.
                          if (zoomState.collapsedNodes.has(nodeId)) {
                            zoomState.toggleNodeCollapse(nodeId);
                          } else {
                            zoomState.toggleManualExpand(nodeId);
                          }
                        }}
                        isSelected={zoomState.selectedNodeId === node.node_id}
                        onSelect={(nodeId) => {
                          zoomState.setSelectedNodeId(nodeId);
                          handleNodeReRoot(nodeId);
                          handleCenterOnReRoot();
                        }}
                      />

                      {/* DEBUG: Coordinate label */}
                      {DEBUG && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            transform: 'translateX(-50%)',
                            fontSize: '10px',
                            color: 'yellow',
                            fontWeight: 'bold',
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            padding: '2px 4px',
                            borderRadius: '2px',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                          }}
                        >
                          ({Math.round(pos.x)},{Math.round(pos.y)})
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Render Empty Slots from unified layout items */}
                {layoutItems.map((item) => {
                  if (item.occupied) return null; // Only render empty slots

                  const parentNode = treeData.getNode(item.parentId);
                  const level = parentNode ? parentNode.depth + 1 : 0;

                  return (
                    <div
                      key={item.id}
                      style={{
                        position: 'absolute',
                        left: item.x,
                        top: item.y,
                        transform: 'translate(-50%, -50%)',
                      }}
                    >
                      <EmptyNode
                        zoom={zoomState.zoom}
                        leg={item.leg}
                        level={level}
                        onPlace={handlePlaceUser}
                        parentId={item.parentId}
                        slotId={item.id}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile: Floating Zoom Controls */}
            {responsive.isMobile && (
              <div className="absolute bottom-20 right-4 flex flex-col gap-2 z-40">
                <button
                  onClick={zoomState.zoomIn}
                  className="w-10 h-10 bg-primary text-on-primary rounded-full shadow-lg flex items-center justify-center text-lg hover:opacity-90 transition-opacity"
                  title="Zoom in"
                >
                  +
                </button>
                <button
                  onClick={zoomState.zoomOut}
                  className="w-10 h-10 bg-primary text-on-primary rounded-full shadow-lg flex items-center justify-center text-lg hover:opacity-90 transition-opacity"
                  title="Zoom out"
                >
                  −
                </button>
                <button
                  onClick={handleFitToView}
                  className="w-10 h-10 bg-primary text-on-primary rounded-full shadow-lg flex items-center justify-center text-lg hover:opacity-90 transition-opacity"
                  title="Fit to view"
                >
                  ⌂
                </button>
              </div>
            )}

            </div>
          </div>
        </div>
      )}

      {/* Mobile: Bottom Sheet */}
      {responsive.isMobile && (
        <DetailsBottomSheet
          node={selectedNode}
          treeData={treeData}
          isOpen={!!selectedNode}
          onClose={() => zoomState.setSelectedNodeId(null)}
        />
      )}

      {/* Desktop: Detail Panel Overlay */}
      {!responsive.isMobile && (
        <NodeDetailsPanel
          node={selectedNode}
          treeData={treeData}
          isOpen={desktopDetailOpen}
          onClose={() => setDesktopDetailOpen(false)}
          isSuperAdmin={user?.role === 'superadmin'}
          onActionComplete={fetchTree}
        />
      )}

      {/* Placement Dialog */}
      {placementDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-lg font-bold mb-4 text-on-surface">
                Place User in Slot
              </h2>
              <p className="text-sm text-on-surface/70 mb-4">
                Leg {placementDialog.leg}, Level {Math.round((layoutItems.find(i => i.id === placementDialog.slotId)?.y ?? 0) / (responsive.isMobile ? 80 : 120))}
              </p>

              {approvedUsers.length === 0 ? (
                <p className="text-sm text-on-surface/70 py-4">No approved users available for placement</p>
              ) : (
                <div className="max-h-64 overflow-y-auto border border-outline rounded mb-4">
                  {approvedUsers.map(approvedUser => (
                    <button
                      key={approvedUser.id}
                      onClick={() => confirmPlacement(approvedUser.id)}
                      disabled={placingUser === approvedUser.id}
                      className="w-full text-left px-4 py-3 hover:bg-primary/10 border-b border-outline last:border-b-0 transition-colors disabled:opacity-50"
                    >
                      <div className="font-medium text-on-surface">
                        {approvedUser.wellis_id}
                        {approvedUser.pre_processed && (
                          <span className="ml-2 text-xs px-2 py-0.5 rounded bg-primary/10 text-primary">Pre-processed</span>
                        )}
                      </div>
                      <div className="text-xs text-on-surface/60">{approvedUser.name || approvedUser.email}</div>
                    </button>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => setPlacementDialog(null)}
                  className="flex-1 px-4 py-2 bg-surface border border-outline rounded hover:bg-surface/80 transition-colors text-on-surface"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
      <PlacementWelcomeModal isOpen={showWelcomeModal} onClose={() => setShowWelcomeModal(false)} />
        </div>
      )}
    </div>
  );
}
